import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { HelpCircle, ArrowLeft, Mail, Clock, CheckCircle, AlertCircle, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';

const AdminSupportTickets = () => {
  const navigate = useNavigate();
  const { user, API, language } = useContext(AuthContext);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  const content = {
    en: {
      title: 'Support Tickets',
      back: 'Back',
      allStatus: 'All Status',
      new: 'New',
      inProgress: 'In Progress',
      resolved: 'Resolved',
      totalTickets: 'Total Tickets',
      from: 'From',
      subject: 'Subject',
      message: 'Message',
      status: 'Status',
      created: 'Created',
      actions: 'Actions',
      markAs: 'Mark as',
      noTickets: 'No support tickets yet',
      delete: 'Delete',
      deleteTitle: 'Delete Support Ticket',
      deleteMessage: 'Are you sure you want to delete this support ticket? This action cannot be undone.',
      cancel: 'Cancel',
      confirmDelete: 'Delete Ticket'
    },
    de: {
      title: 'Support-Tickets',
      back: 'Zurück',
      allStatus: 'Alle Status',
      new: 'Neu',
      inProgress: 'In Bearbeitung',
      resolved: 'Gelöst',
      totalTickets: 'Gesamttickets',
      from: 'Von',
      subject: 'Betreff',
      message: 'Nachricht',
      status: 'Status',
      created: 'Erstellt',
      actions: 'Aktionen',
      markAs: 'Markieren als',
      noTickets: 'Noch keine Support-Tickets',
      delete: 'Löschen',
      deleteTitle: 'Support-Ticket löschen',
      deleteMessage: 'Möchten Sie dieses Support-Ticket wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
      cancel: 'Abbrechen',
      confirmDelete: 'Ticket löschen'
    }
  };

  const t = content[language] || content.en;

  useEffect(() => {
    if (!user?.is_admin) {
      toast.error('Access denied. Admin only.');
      navigate('/');
      return;
    }
    fetchTickets();
  }, [statusFilter]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const response = await axios.get(`${API}/support/admin/tickets${params}`);
      setTickets(response.data.tickets || []);
    } catch (error) {
      toast.error('Failed to load support tickets');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (ticketId, newStatus) => {
    try {
      await axios.put(`${API}/support/admin/tickets/${ticketId}`, { status: newStatus });
      toast.success('Ticket status updated');
      fetchTickets();
    } catch (error) {
      toast.error('Failed to update status');
      console.error(error);
    }
  };

  const handleDeleteTicket = async () => {
    try {
      await axios.delete(`${API}/support/admin/tickets/${selectedTicket.id}`);
      toast.success('Ticket deleted successfully');
      setDeleteDialogOpen(false);
      fetchTickets();
    } catch (error) {
      toast.error('Failed to delete ticket');
      console.error(error);
    }
  };

  const openDeleteDialog = (ticket) => {
    setSelectedTicket(ticket);
    setDeleteDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    const colors = {
      new: 'bg-blue-600',
      in_progress: 'bg-orange-600',
      resolved: 'bg-green-600'
    };
    return (
      <Badge className={colors[status] || 'bg-gray-600'}>
        {status === 'new' ? t.new : status === 'in_progress' ? t.inProgress : t.resolved}
      </Badge>
    );
  };

  const getStatusIcon = (status) => {
    if (status === 'new') return <AlertCircle className="w-4 h-4" />;
    if (status === 'in_progress') return <Clock className="w-4 h-4" />;
    return <CheckCircle className="w-4 h-4" />;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString(language === 'de' ? 'de-DE' : 'en-US');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <HelpCircle className="w-8 h-8 text-blue-600" />
            <h1 className="text-2xl font-bold font-heading">{t.title}</h1>
          </div>
          <Button variant="outline" onClick={() => navigate('/admin/dashboard')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t.back}
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filter */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>{t.totalTickets}: {tickets.length}</CardTitle>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t.allStatus}</SelectItem>
                  <SelectItem value="new">{t.new}</SelectItem>
                  <SelectItem value="in_progress">{t.inProgress}</SelectItem>
                  <SelectItem value="resolved">{t.resolved}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
        </Card>

        {/* Tickets List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : tickets.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              {t.noTickets}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {tickets.map((ticket) => (
              <Card key={ticket.id} className="hover-lift">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {getStatusIcon(ticket.status)}
                        <h3 className="text-lg font-bold">{ticket.title}</h3>
                        {getStatusBadge(ticket.status)}
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                        <div className="flex items-center gap-1">
                          <Mail className="w-4 h-4" />
                          <span><strong>{t.from}:</strong> {ticket.user_name} ({ticket.user_email})</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          <span>{formatDate(ticket.created_at)}</span>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <p className="text-sm"><strong>{t.subject}:</strong> {ticket.subject}</p>
                        <p className="text-sm text-gray-600">{ticket.message}</p>
                      </div>
                    </div>

                    <div className="ml-4 flex gap-2">
                      <Select
                        value={ticket.status}
                        onValueChange={(value) => updateStatus(ticket.id, value)}
                      >
                        <SelectTrigger className="w-40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">{t.new}</SelectItem>
                          <SelectItem value="in_progress">{t.inProgress}</SelectItem>
                          <SelectItem value="resolved">{t.resolved}</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => openDeleteDialog(ticket)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.deleteTitle}</DialogTitle>
            <DialogDescription>{t.deleteMessage}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              {t.cancel}
            </Button>
            <Button variant="destructive" onClick={handleDeleteTicket}>
              {t.confirmDelete}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSupportTickets;
